import { MB_SVG_ChevronDown } from '@mightybyte/rnw.components.svg-components';
import React, { useImperativeHandle, useState } from 'react';
import { Animated, StyleProp, ViewStyle } from 'react-native';


interface MB_AnimatedChevronRef {
    setToInitialRotation: () => void,
    setToFinalRotation: () => void
};

interface MB_AnimatedChevronProps {
    /**
     * The initial rotation of the chevron in degrees (0 to 360).
     * 
     * @default 0
     */
    startingRotation?: number,
    
    /**
     * The final rotation of the chevron in degrees (0 to 360).
     * 
     * @default 180
     */
    finalRotation?: number,
    
    /**
     * The size of the chevron.
     * 
     * @default 20
     */
    chevronSize?: number,
    
    /**
     * The color of the chevron.
     * 
     * @default 'white'
     */
    chevronColor?: string,
    
    /**
     * The style of the chevron container.
     */
    containerStyle?: StyleProp<ViewStyle>,
    
    /**
     * The chevron's animation duration in milliseconds.
     * 
     * @default 150
     */
    duration?: number,
}

const MB_AnimatedChevron = React.forwardRef<MB_AnimatedChevronRef, MB_AnimatedChevronProps>(({
    startingRotation = 0,
    finalRotation = 180,
    chevronSize = 20,
    chevronColor = 'white',
    containerStyle,
    duration = 150,
}, ref) => {
    const [animatedValue] = useState(new Animated.Value(startingRotation));

    const animatedInterpolation = animatedValue.interpolate({
        inputRange: [0, 360],
        outputRange: ['0deg', '360deg'],
    });

    const animate = (animateValue: number) => {
        Animated.timing(animatedValue, {
            toValue: animateValue,
            duration,
            useNativeDriver: false,
        }).start();
    };


    useImperativeHandle(ref, () => ({
        setToInitialRotation: () => animate(startingRotation),
        setToFinalRotation: () => animate(finalRotation),
    }));

    return (
        <Animated.View style={[containerStyle, { transform: [{ rotate: animatedInterpolation }] }]}>
            <MB_SVG_ChevronDown color={chevronColor} size={chevronSize}/>
        </Animated.View>
    );
});


export { MB_AnimatedChevron };
export type { MB_AnimatedChevronRef, MB_AnimatedChevronProps };
